import { z } from "zod";
import { sectionAddedPayloadSchema, sectionPromptChangedPayloadSchema, sectionAudioBoundPayloadSchema, sectionAudioReboundPayloadSchema, sectionVariationsGeneratedPayloadSchema, sectionVariationPromotedPayloadSchema, sectionGenreShiftedPayloadSchema, } from "./musical/section.js";
import { referenceUploadedPayloadSchema, referenceAnalyzedPayloadSchema, referenceRemovedPayloadSchema, } from "./musical/reference.js";
import { stemMutedPayloadSchema, stemUnmutedPayloadSchema, stemVolumeChangedPayloadSchema, sectionStemsBoundPayloadSchema, } from "./musical/stem.js";
import { sectionEnvelopeExtractedPayloadSchema } from "./musical/envelope.js";
import { masterEqAdjustedPayloadSchema } from "./musical/mix.js";
import { dnaReferenceAttachedPayloadSchema } from "./musical/dna.js";
import { bandSonicTraitsOverriddenPayloadSchema } from "./musical/sonic-traits.js";
import { generationRequestedPayloadSchema, generationProgressPayloadSchema, generationCompletedPayloadSchema, jobFailedPayloadSchema, dnaAnalysisCompletedPayloadSchema, exportCompletedPayloadSchema, sectionRegenerationRequestedPayloadSchema, stemsExtractedPayloadSchema, envelopeExtractedPayloadSchema, clipRenderedPayloadSchema, } from "./operational/jobs.js";
import { costIncurredPayloadSchema, costCapExceededPayloadSchema } from "./operational/cost.js";
import { historyBranchCreatedPayloadSchema } from "./operational/history.js";
export const musicalPayloadByType = {
    SectionAdded: sectionAddedPayloadSchema,
    SectionPromptChanged: sectionPromptChangedPayloadSchema,
    SectionAudioBound: sectionAudioBoundPayloadSchema,
    SectionAudioRebound: sectionAudioReboundPayloadSchema,
    SectionVariationsGenerated: sectionVariationsGeneratedPayloadSchema,
    SectionVariationPromoted: sectionVariationPromotedPayloadSchema,
    StemMuted: stemMutedPayloadSchema,
    StemUnmuted: stemUnmutedPayloadSchema,
    StemVolumeChanged: stemVolumeChangedPayloadSchema,
    SectionStemsBound: sectionStemsBoundPayloadSchema,
    SectionEnvelopeExtracted: sectionEnvelopeExtractedPayloadSchema,
    MasterEqAdjusted: masterEqAdjustedPayloadSchema,
    DNAReferenceAttached: dnaReferenceAttachedPayloadSchema,
    BandSonicTraitsOverridden: bandSonicTraitsOverriddenPayloadSchema,
    ReferenceUploaded: referenceUploadedPayloadSchema,
    ReferenceAnalyzed: referenceAnalyzedPayloadSchema,
    ReferenceRemoved: referenceRemovedPayloadSchema,
    SectionGenreShifted: sectionGenreShiftedPayloadSchema,
};
export const operationalPayloadByType = {
    GenerationRequested: generationRequestedPayloadSchema,
    GenerationProgress: generationProgressPayloadSchema,
    GenerationCompleted: generationCompletedPayloadSchema,
    JobFailed: jobFailedPayloadSchema,
    DNAAnalysisCompleted: dnaAnalysisCompletedPayloadSchema,
    ExportCompleted: exportCompletedPayloadSchema,
    SectionRegenerationRequested: sectionRegenerationRequestedPayloadSchema,
    StemsExtracted: stemsExtractedPayloadSchema,
    EnvelopeExtracted: envelopeExtractedPayloadSchema,
    ClipRendered: clipRenderedPayloadSchema,
    CostIncurred: costIncurredPayloadSchema,
    CostCapExceeded: costCapExceededPayloadSchema,
    HistoryBranchCreated: historyBranchCreatedPayloadSchema,
};
export const MUSICAL_EVENT_TYPES = Object.keys(musicalPayloadByType);
export const OPERATIONAL_EVENT_TYPES = Object.keys(operationalPayloadByType);
/**
 * Current schema version per event type. The schema in `musicalPayloadByType` /
 * `operationalPayloadByType` always describes this version. Producers (the
 * command interpreter, the worker) must stamp newly-created events with this
 * version so the replay upcaster ladder does not treat a current event as a
 * legacy one.
 *
 * Any event type absent from this map is at version 1. A type appears here only
 * once its schema has been bumped past v1 (e.g. `SectionAdded` -> v2 in Plan 11,
 * which added `role`/`order`).
 */
const EVENT_VERSION_OVERRIDES = {
    SectionAdded: 2,
    DNAReferenceAttached: 2,
};
/** Returns the current schema version for an event type (defaults to 1). */
export function currentEventVersion(type) {
    return EVENT_VERSION_OVERRIDES[type] ?? 1;
}
export const envelopeShapeSchema = z.object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    type: z.string().min(1),
    version: z.number().int().positive(),
    category: z.enum(["musical", "operational"]),
    payload: z.unknown(),
    prevHash: z.string(),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    createdAt: z.string().datetime(),
    createdBy: z.enum(["user", "ai", "system"]),
});
//# sourceMappingURL=schemas.js.map